// Create a Field Worker account. POST /workers/.
//
// Skills are picked from the real department list rather than typed, because
// assignment substring-matches this text against a complaint's department — see
// lib/skills.js. Free text let an admin create a worker who could never be
// assigned anything, with nothing on screen explaining why.
//
// The password is optional and generated by default. Creation therefore has two
// screens rather than one: the form, and then what happened to the credentials.
// The second one matters — an account nobody was told about is not a finished
// job, and that fact does not survive a toast.
import React, { useState } from 'react';
import Modal from './Modal';
import SkillPicker from './SkillPicker';
import CredentialDeliveryNote from './CredentialDeliveryNote';
import {
  Field, TextField, FormError, SubmitRow, NewAccountPassword, MIN_NEW_PASSWORD,
} from './formFields';
import { createFieldWorker } from '../../api/workers';
import { formatSkills } from '../../lib/skills';

export default function CreateWorkerModal({ onDismiss, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [generatePassword, setGeneratePassword] = useState(true);
  const [skills, setSkills] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Set once the account exists. Its presence is what swaps the form for the
  // confirmation, so the administrator cannot submit twice.
  const [created, setCreated] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError('Name, email and phone are all required.');
      return;
    }
    if (!generatePassword && form.password.length < MIN_NEW_PASSWORD) {
      setError(`A password you choose needs at least ${MIN_NEW_PASSWORD} characters.`);
      return;
    }
    if (skills.length === 0) {
      setError('Pick at least one department — a worker with no skills is never offered any work.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const result = await createFieldWorker({
        name: form.name,
        email: form.email,
        phone: form.phone,
        // Omitted entirely when generating, so the server picks one.
        password: generatePassword ? undefined : form.password,
        skillSet: formatSkills(skills),
      });
      setCreated(result);
    } catch (err) {
      if (err.name !== 'SessionExpiredError') setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // The account is already saved by this point; "Done" only closes the modal
  // and refreshes the list behind it.
  const finish = () => onCreated(`${form.name} added as a Field Worker.`);

  if (created) {
    return (
      <Modal title="Field worker created" onDismiss={finish}>
        <div className="space-y-3.5">
          <p className="text-[13.5px] leading-snug text-ink-body">
            <strong className="text-ink">{form.name}</strong> is on the roster and can be
            assigned complaints in {formatSkills(skills)}.
          </p>

          <CredentialDeliveryNote
            delivery={created.credentialDelivery}
            name={form.name}
          />

          <button
            type="button"
            onClick={finish}
            className="focus-ring w-full rounded-lg bg-leaf-600 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-colors hover:bg-leaf-700"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Add Field Worker" onDismiss={onDismiss}>
      <form onSubmit={submit} className="space-y-3.5">
        <TextField
          id="fw-name"
          label="Full name"
          required
          value={form.name}
          onChange={set('name')}
        />
        <TextField
          id="fw-email"
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={set('email')}
          hint="This is how they sign in, and where their password is sent. It must be unique."
        />
        <TextField
          id="fw-phone"
          label="Phone"
          required
          value={form.phone}
          onChange={set('phone')}
        />

        <NewAccountPassword
          idPrefix="fw"
          generate={generatePassword}
          onGenerateChange={setGeneratePassword}
          value={form.password}
          onChange={set('password')}
        />

        <Field id="fw-skills" label="Skills" required
          hint="The departments whose complaints this worker can be assigned. A worker with none is never offered work.">
          <SkillPicker value={skills} onChange={setSkills} />
        </Field>

        <FormError>{error}</FormError>

        <SubmitRow
          busy={saving}
          label="Create field worker"
          busyLabel="Creating…"
          onCancel={onDismiss}
        />
      </form>
    </Modal>
  );
}
